import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { decodeRoutineHash } from '../features/share/encodeShare';
import { ShareDecodeError, type SharedRoutineV1 } from '../features/share/types';
import { addRoutine, nextLibrarySortIndex } from '../features/profiles/routineMutations';
import { bySortIndex } from '../lib/sortIndex';
import type { Routine, Task } from '../lib/types';
import { uid } from '../lib/uid';
import { useSyncedStore } from '../store/synced';

// /share landing route per plan/02 §B. Share a single routine. The kid
// (or parent) tapping the link sees a preview, picks which child to add
// it to, and lands on that profile's routine selector. Not PIN-gated by
// design — the only mutation is "add", never replace or delete.
export function ShareImportPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const tree = useSyncedStore((s) => s.tree);
  const setTree = useSyncedStore((s) => s.setTree);

  const decoded = useMemo<
    { ok: true; payload: SharedRoutineV1 } | { ok: false; error: string }
  >(() => {
    try {
      return { ok: true, payload: decodeRoutineHash(location.hash) };
    } catch (e) {
      if (e instanceof ShareDecodeError) return { ok: false, error: e.message };
      throw e;
    }
  }, [location.hash]);

  const cancel = () => {
    history.replaceState(null, '', '/');
    navigate('/', { replace: true });
  };

  if (!decoded.ok) {
    return (
      <main className="share-import">
        <h1>Linkki ei toimi</h1>
        <p className="share-import-error">Linkki on virheellinen tai vanhentunut.</p>
        <Link to="/" replace className="settings-button" onClick={cancel}>
          Etusivulle
        </Link>
      </main>
    );
  }

  const { payload } = decoded;
  const profiles = (tree?.profiles ?? []).slice().sort(bySortIndex);

  const onPick = (profileId: string) => {
    if (!tree) return;
    const targetProfile = tree.profiles.find((p) => p.id === profileId);
    if (!targetProfile) return;
    const ts = new Date().toISOString();
    const tasks: Task[] = payload.routine.tasks.map((t, i) => ({
      id: uid(),
      name: t.name,
      emoji: t.emoji,
      tag: t.tag,
      sortIndex: i,
      createdAt: ts,
      updatedAt: ts,
    }));
    const newRoutine: Routine = {
      id: uid(),
      name: payload.routine.name,
      emoji: payload.routine.emoji,
      sortIndex: nextLibrarySortIndex(tree),
      tasks,
      createdAt: ts,
      updatedAt: ts,
    };
    setTree(addRoutine(tree, profileId, newRoutine, ts));
    history.replaceState(null, '', `/p/${profileId}`);
    navigate(`/p/${profileId}`, { replace: true });
  };

  return (
    <main className="share-import">
      <h1>Tuo rutiini</h1>
      <div className="share-import-preview">
        <span className="share-import-emoji" aria-hidden="true">
          {payload.routine.emoji}
        </span>
        <span className="share-import-name">{payload.routine.name}</span>
      </div>
      <ul className="share-import-tasks">
        {payload.routine.tasks.map((t, i) => (
          <li key={i}>
            <span aria-hidden="true">{t.emoji}</span> {t.name}
          </li>
        ))}
      </ul>
      <h2 className="share-import-subtitle">Valitse profiili:</h2>
      <ul className="profile-grid">
        {profiles.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              className="profile-tile"
              onClick={() => onPick(p.id)}
              aria-label={`Lisää profiilille: ${p.name}`}
            >
              <span aria-hidden="true">{p.avatarEmoji}</span>
              <span>{p.name}</span>
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="settings-button" onClick={cancel}>
        Peruuta
      </button>
    </main>
  );
}
